import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import ReactPaginate from "react-paginate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";

import {
  fetchOpportunitiesSummary,
  updateOpportunityJobStatus,
} from "../../store/reducer/opportunitySummary/opportunitySummarySlice";
import { getDdOptionForUI } from "../../store/reducer/ddOption/ddOptionSlice";
import { getOpportunityIdsForFinancials, getOpportunityIdsForSalePerson } from "../../store/reducer/opportunity/opportunitySlice";

/* ------------------------------------------------------------------ */
const asGBP = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  }).format(Number(n || 0));
const asPct = (n) => `${Number(n || 0).toFixed(2)}%`;

const LIMIT = 55;

export default function OpportunityOverview() {
  const dispatch = useDispatch();
  const { options = [] } = useSelector((s) => s.ddOptions);
  const { loading } = useSelector((s) => s.opportunitySummary);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  // Filters / option lists
  const [oppSelectOptions, setOppSelectOptions] = useState([]);
  const [oppSelectSalePersonOptions, setSalePersonSelectOptions] = useState([]);

  const [selectedOpp, setSelectedOpp] = useState("");
  const [selectedOppPerson, setSelectedOppPerson] = useState("");
  const [selectedOppFinancial, setSelectedOppFinancial] = useState("");
  const [selectedOppStatus, setSelectedOppStatus] = useState("");

  /* ---------------- load summary data ---------------- */
  const loadPage = useCallback(
    (p) => {
      const hasAnyFilter =
        selectedOpp || selectedOppPerson || selectedOppFinancial || selectedOppStatus;

      const params = {
        page: p,
        limit: LIMIT,
        ...(selectedOpp ? { opportunity_id: selectedOpp } : {}),
        ...(selectedOppPerson ? { person_id: selectedOppPerson } : {}),
        ...(selectedOppFinancial ? { financial_id: selectedOppFinancial } : {}),
        ...(selectedOppStatus ? { status: selectedOppStatus } : {}),
      };

      dispatch(fetchOpportunitiesSummary(params))
        .unwrap()
        .then(({ rows, count }) => {
          setRows(rows || []);
          setTotal(Number(count || 0));
          setPage(p);
        })
        .catch(() => {
          rows.length && setRows([]);
          total && setTotal(0);
          page !== 1 && setPage(1);
          toast.error("Could not load opportunities");
        });
    },
    [dispatch, selectedOpp, selectedOppPerson, selectedOppFinancial, selectedOppStatus, rows.length, total, page]
  );

  /* --------------- load filter options & initial data ---------------- */
  useEffect(() => {
    dispatch(getDdOptionForUI());
    dispatch(getOpportunityIdsForFinancials()).then((res) => {
      if (res?.payload) setOppSelectOptions(res.payload);
    });
    dispatch(getOpportunityIdsForSalePerson()).then((res) => {
      if (res?.payload) setSalePersonSelectOptions(res.payload);
    });
  }, [dispatch]);

  /* -------------- reload when filter or page changes ---------------- */
  useEffect(() => {
    // Whenever any filter changes, reset to page 1
    loadPage(1);
  }, [loadPage]);

  /* ---------------- handle dropdown change ---------------- */
  const handleOppChange = (e) => setSelectedOpp(e.target.value);
  const handleOppChangePerson = (e) => setSelectedOppPerson(e.target.value);
  const handleOppChangeFinancial = (e) => setSelectedOppFinancial(e.target.value);
  const handleOppChangeStatus = (e) => setSelectedOppStatus(e.target.value);

  /* ---------------- job status change ------------------ */
  const handleJobStatusChange = (id, status) => {
    setRows((prev) =>
      prev.map((r) => {
        const oppId = r?.opportunity_id ?? r?.id;
        return oppId === id ? { ...r, job_status: status, job_id: status } : r;
      })
    );
    dispatch(updateOpportunityJobStatus({ opportunity_id: id, status }))
      .unwrap()
      .then(() => toast.success("Job status updated"))
      .catch(() => toast.error("Failed to update job status"));
  };

  const statusLabel = (id) => options.find((o) => o.id === id)?.field_code || "";

  const anyFilterActive =
    !!selectedOpp || !!selectedOppPerson || !!selectedOppFinancial || !!selectedOppStatus;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <label htmlFor="oppFilter" className="font-medium">
          Opportunity:
        </label>
        <select
          id="oppFilter"
          className="border border-gray-300 rounded p-2"
          style={{ minWidth: 280 }}
          value={selectedOpp}
          onChange={handleOppChange}
        >
          <option value="">All Opportunities</option>
          {oppSelectOptions.map((o) => (
            <option
              key={`${o.opportunity_id}-${o.drawing_id}`}
              value={o.opportunity_id}
            >
              {`${o.opportunity_id} - ${o.drawing_id} - ${o.customer_name} - ${o.site_name}`}
            </option>
          ))}
        </select>

        <label htmlFor="oppFilter3" className="font-medium">
          Sales Person:
        </label>
        <select
          id="oppFilter3"
          className="border border-gray-300 rounded p-2"
          value={selectedOppPerson}
          onChange={handleOppChangePerson}
        >
          <option value="">Select Person</option>
          {oppSelectSalePersonOptions.map((o) => (
            <option key={`${o.id}`} value={o.firstname}>
              {`${o.firstname} - ${o.lastname}`}
            </option>
          ))}
        </select>

        <label htmlFor="oppFilter2" className="font-medium">
          Financial Year:
        </label>
        <select
          id="oppFilter2"
          className="border border-gray-300 rounded p-2"
          value={selectedOppFinancial}
          onChange={handleOppChangeFinancial}
        >
          <option value="">Select Year</option>
          <option value="2024-08-01/2025-07-31">(1/8/2024 – 31/7/2025)</option>
          <option value="2025-08-01/2026-07-31">(1/8/2025 – 31/7/2026)</option>
          <option value="2026-08-01/2027-07-31">(1/8/2026 – 31/7/2027)</option>
        </select>

        <label htmlFor="oppFilterStatus" className="font-medium">
          Job Status:
        </label>
        <select
          id="oppFilterStatus"
          value={selectedOppStatus}
          className="border border-gray-300 rounded p-2"
          onChange={handleOppChangeStatus}
        >
          <option value="">Select</option>
          {options
            .filter(
              (o) =>
                o.ui_name === "Opportunity Summary" &&
                o.field_name === "job_status"
            )
            .sort((a, b) => a.sort_field - b.sort_field)
            .map((o) => (
              <option key={o.id} value={o.id}>
                {o.field_code}
              </option>
            ))}
        </select>
      </div>

      <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
        <h2 className="px-6 py-4 text-2xl font-semibold">
          Job Progress Overview
        </h2>
        <table className="table-auto w-full divide-y divide-gray-200 rounded-lg">
          <thead className="bg-gray-100 sticky top-0">
            <tr>
              <th className="px-4 py-3 text-left font-medium uppercase text-xs">
                Customer Name
              </th>
              <th className="px-4 py-3 text-left font-medium uppercase text-xs">
                Site Name
              </th>
              <th className="px-4 py-3 text-left font-medium uppercase text-xs">
                Opp ID
              </th>
              <th className="px-4 py-3 text-left font-medium uppercase text-xs">
                Drawing ID
              </th>
              <th className="px-4 py-3 text-left font-medium uppercase text-xs">
                Progress Status
              </th>
              <th
                colSpan={4}
                className="px-4 py-3 text-center font-medium uppercase text-xs"
              >
                Budgeted
              </th>
              <th
                colSpan={6}
                className="px-4 py-3 text-center font-medium uppercase text-xs"
              >
                Actuals
              </th>
            </tr>
            <tr className="bg-gray-50">
              {[...Array(5)].map((_, i) => (
                <th key={i} className="px-4 py-2" />
              ))}
              {[
                "Revenue",
                "Costs",
                "Profit",
                "GP %",
                "Approved PO",
                "Revenue",
                "Costs",
                "Profit",
                "GP %",
                "Job Status",
              ].map((h, i) => (
                <th
                  key={`${h}-${i}`}
                  className="px-4 py-2 text-right font-medium uppercase text-xs"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={16} className="text-center py-12">
                  <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                </td>
              </tr>
            ) : rows.length > 0 ? (
              rows.map((r, idx) => {
                const oppId = r?.opportunity_id ?? r?.id;
                const budgetRevenue = r?.budget_revenue ?? 0;
                const budgetCost = r?.budget_cost ?? r?.budget_costs ?? 0;
                const budgetProfit = r?.budget_profit ?? 0;
                const budgetGp = r?.budget_gp ?? 0;

                const approvedPO = r?.approved_po_value ?? null;

                const actualRevenue = r?.actual_revenue ?? 0;
                const actualCost = r?.actual_cost ?? r?.actual_costs ?? 0;
                const actualProfit = r?.actual_profit ?? 0;
                const actualGp = r?.actual_gp ?? 0;

                const jobStatus = r?.job_status ?? r?.job_id ?? "";

                return (
                  <tr
                    key={`${oppId}-${r.drawing_id}`}
                    className={`hover:bg-gray-50 ${
                      idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                    }`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.customer_name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{r.site_name}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{oppId}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.drawing_id}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.progress_status_name || ""}
                    </td>

                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {asGBP(budgetRevenue)}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {asGBP(budgetCost)}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {asGBP(budgetProfit)}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {asPct(budgetGp)}
                    </td>

                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {approvedPO ? asGBP(approvedPO) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {asGBP(actualRevenue)}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {asGBP(actualCost)}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {asGBP(actualProfit)}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {asPct(actualGp)}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      {options.length ? (
                        <select
                          className="w-full bg-white border border-gray-300 rounded p-2 text-xs"
                          value={jobStatus || ""}
                          onChange={(e) =>
                            handleJobStatusChange(oppId, Number(e.target.value))
                          }
                          style={{ width: "130px" }}
                        >
                          <option value="">Select</option>
                          {options
                            .filter(
                              (o) =>
                                o.ui_name === "Opportunity Summary" &&
                                o.field_name === "job_status"
                            )
                            .sort((a, b) => a.sort_field - b.sort_field)
                            .map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.field_code}
                              </option>
                            ))}
                        </select>
                      ) : (
                        statusLabel(jobStatus)
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={16} className="text-center py-12 text-gray-500">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {total > 0 && !anyFilterActive && (
        <div className="flex flex-col md:flex-row justify-between items-center bg-gray-100 rounded-lg p-4">
          <div className="mb-2 md:mb-0 text-sm text-gray-600">
            Showing <b>{page}</b> of <b>{Math.ceil(total / LIMIT)}</b> pages
          </div>

          <ReactPaginate
            breakLabel={<span className="mr-2">...</span>}
            nextLabel={<FontAwesomeIcon icon={faAngleRight} />}
            previousLabel={<FontAwesomeIcon icon={faAngleLeft} />}
            onPageChange={({ selected }) => loadPage(selected + 1)}
            forcePage={page - 1}
            pageCount={Math.ceil(total / LIMIT)}
            containerClassName="flex space-x-2"
            pageClassName="px-3 py-1 rounded hover:bg-gray-200"
            activeClassName="bg-blue-600 text-white"
          />
        </div>
      )}
    </div>
  );
}
